import JsonLd from "@/components/JsonLd.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import ctaBg from '../landing/assets/about_us_4.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import featuresDiagram from '../landing/assets/features_diagram.png';
import badminton from '../landing/assets/sports/badminton.jpg';
import padel from '../landing/assets/sports/padel.jpg';
import pickleball from '../landing/assets/sports/pickleball.jpg';
import squash from '../landing/assets/sports/squash.jpg';
import tennis from '../landing/assets/sports/tennis.jpg';
import infographic from '../landing/assets/Howitworks.jpg';
import sportsLeagueFeaturesBg from '../landing/assets/sports/League2.png';
import sportsLeagueHeroBg from '../landing/assets/sports_league_hero_bg.webp';
import sportsLeagueNextLevelBg from '../landing/assets/Players2.jpg';
import sportsLeagueWhyBg from '../landing/assets/sports_league_why_bg.webp';
import sportsLeagueIcon1 from '../landing/assets/svgs/scheduling_icon.svg';
import sportsLeagueIcon2 from '../landing/assets/svgs/sports_league_icon_2.svg';
import sportsLeagueIcon3 from '../landing/assets/svgs/sports_league_icon_3.svg';
import sportsLeagueIcon4 from '../landing/assets/svgs/sports_league_icon_4.svg';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const SPORTS = [
    {title: "Padel", backgroundImage: padel, href: "/padel-tournament-software/"},
    {title: "Tennis", backgroundImage: tennis, href: "/tennis-tournament-software/"},
    {title: "Pickleball", backgroundImage: pickleball, href: "/pickleball-tournament-software/"},
    {title: "Badminton", backgroundImage: badminton, href: "/badminton-tournament-software/"},
    {title: "Squash", backgroundImage: squash, href: "/squash-tournament-software/"},
];

const FAQs = [
    {
        q: "What is sports tournament software, and how does ScorePal help?",
        a: <>Sports tournament software helps clubs and organizers manage competitions digitally. It simplifies tournament creation, scheduling, bracket generation, live scoring, standings, and result management from one centralized platform.</>,
    },
    {
        q: "How does tournament management software work?",
        a: <>Tournament management software automates the process of running competitions. Organizers can create tournaments, add teams, choose formats, generate brackets, schedule matches, track scores live, and publish results automatically.</>,
    },
    {
        q: "What tournament formats does ScorePal support?",
        a: <>ScorePal supports multiple competition formats including knockout tournaments, round robin formats, group stages, league structures, and hybrid tournament formats.</>,
    },
    {
        q: "Does ScorePal include sports bracket software?",
        a: <>Yes. ScorePal includes built-in sports bracket software that automatically generates brackets and updates tournament progression in real time as match results are entered.</>,
    },
    {
        q: "Is ScorePal suitable for growing tournaments and sports organizations?",
        a: <>Yes, ScorePal is designed to scale with your needs. This tournament and league management software supports clubs and organizations of all sizes, helping them manage growth, streamline operations, and even generate revenue through features like sponsorships, events, and merchandise.</>,
    },
    {
        q: "Can I manage leagues and tournaments together?",
        a: <>Yes. ScorePal supports both tournament management and league management within one platform, making it easier for clubs and organizers to manage multiple competitions efficiently.</>,
    },
    {
        q: "Does the platform support live scoring?",
        a: <>Yes. ScorePal includes a live scoring system that updates scores, standings, rankings, and tournament results instantly across devices.</>,
    },
];

export const Route = createFileRoute('/sports-tournament-management-software')({
    component: RouteComponent,
})

function RouteComponent() {
    const features = [
        {
            icon:        sportsLeagueIcon1,
            alt:         'scheduling icon',
            title:       'Create Tournament Formats Instantly',
            description: (
        <>
            Launch competitions in minutes using flexible tournament creation tools designed for modern sports organizers. ScorePal supports knockout tournaments, round robins, league-style formats, and hybrid competition structures from a single dashboard.
            The built-in sports bracket software automatically generates structured matchups while allowing organizers to customize formats based on tournament requirements.
            You can also streamline competition setup using our {' '}

                <a
                    href="https://scorepal.co/create-brackets/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#082F8A] underline font-semibold"
                >
                Bracket Generator 
                </a>
            {' '} for faster tournament creation.
            </>
                ),
            },
        {
            icon:        sportsLeagueIcon2,
            alt:         'scheduling icon',
            title:       'Smart Tournament Scheduling',
            description: (
        <>
            Avoid scheduling headaches with intelligent {' '}

                <a
                    href="https://scorepal.co/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#082F8A] underline font-semibold"
                >
                {' '} tournament scheduling software 
                </a>
            {' '} that helps organizers manage fixtures, venues, and match timings efficiently.
            ScorePal automatically reduces conflicts by organizing matches around team availability, venue allocation, and tournament progression. Clubs can manage multi-day events, divisions, and simultaneous fixtures with ease while keeping schedules updated in real time.
            For organizers running ongoing competitions, our <strong>League Management Software</strong> also supports league fixtures and season planning within the same platform.
            </>
                ),
            },
        {
            icon:        sportsLeagueIcon3,
            alt:         'sports tournament',
            title:       'Real-Time Live Scoring & Match Updates',
            description: 'Keep players, coaches, and spectators informed with a built-in live scoring system that updates match results instantly. ScorePal’s sports competition software allows organizers to track scores live, refresh standings automatically, and display rankings across devices in real time. Match outcomes instantly sync with brackets, schedules, and tournament progress for a seamless experience. The integrated Live Scoring tools help clubs eliminate manual score tracking while improving tournament visibility for participants and fans.',
        },
        {
            icon:        sportsLeagueIcon4,
            alt:         'sports tournament',
            title:       'Centralized Team & Competition Management',
            description: 'Manage teams, players, divisions, registrations, and tournament operations from one easy-to-use dashboard. ScorePal simplifies tournament administration by helping organizers coordinate participants, manage categories, and monitor event progress without switching between tools. Clubs running multiple competitions can handle tournaments and leagues together from one centralized platform. Organizers can also use built-in Sports Analytics features to monitor performance trends, standings, and tournament insights more effectively.',
        },
    ];

    const whyChooseItems = [
        'Easy tournament setup',
        'Flexible competition formats',
        'Automated scheduling',
        'Integrated live scoring',
        'Real-time standings',
        'Mobile-friendly access',
        'Centralized tournament control',
    ];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": [
                    {
                        "@type":          "Question",
                        "name":           "What is sports tournament software, and how does ScorePal help?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Sports tournament software helps clubs and organizers manage competitions digitally. It simplifies tournament creation, scheduling, bracket generation, live scoring, standings, and result management from one centralized platform."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "How does tournament management software work?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Tournament management software automates the process of running competitions. Organizers can create tournaments, add teams, choose formats, generate brackets, schedule matches, track scores live, and publish results automatically."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "What tournament formats does ScorePal support?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "ScorePal supports multiple competition formats including knockout tournaments, round robin formats, group stages, league structures, and hybrid tournament formats."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does ScorePal include sports bracket software?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. ScorePal includes built-in sports bracket software that automatically generates brackets and updates tournament progression in real time as match results are entered."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Is ScorePal suitable for growing tournaments and sports organizations?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, ScorePal is designed to scale with your needs. This tournament and league management software supports clubs and organizations of all sizes, helping them manage growth, streamline operations, and even generate revenue through features like sponsorships, events, and merchandise."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Is ScorePal suitable for growing tournaments and sports organizations?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, ScorePal is designed to scale with your needs. This tournament and tournament management software supports clubs and organizations of all sizes, helping them manage growth, streamline operations, and even generate revenue through features like sponsorships, events, and merchandise."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can I manage leagues and tournaments together?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. ScorePal supports both tournament management and league management within one platform, making it easier for clubs and organizers to manage multiple competitions efficiently."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does the platform support live scoring?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes. ScorePal includes a live scoring system that updates scores, standings, rankings, and tournament results instantly across devices."
                        }
                    }
                ]
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context":            "https://schema.org",
                "@type":               "SoftwareApplication",
                "name":                "Sports Tournament Management Software",
                "url":                 "https://scorepal.co/sports-tournament-management-software/",
                "applicationCategory": "SportsApplication",
                "operatingSystem":     ["Web", "Android", "iOS"],
                "description":         "Manage sports tournaments with automated fixtures, standings, team tracking, and scheduling tools designed for clubs and organizers. Proven performance over the past 2 years.",
                "offers":              {
                    "@type":         "Offer",
                    "price":         "0",
                    "priceCurrency": "USD"
                }
            }} />
            <title>Sports Tournament Software for Clubs & Organizers | All-in-One Tournament Platform</title>
            <meta name="description" content="Run tournaments effortlessly with ScorePal tournament management software featuring bracket generator tools, tournament scheduling, and live scoring system for clubs & organizers." />
            <link rel="canonical" href={`${config.publicUrl}/sports-tournament-management-software/`} />

            <section className="relative overflow-hidden p-4">
                <div
                    className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-top bg-cover bg-no-repeat saturate-[0.87]"
                    style={{
                        backgroundImage: `url(${sportsLeagueHeroBg})`
                    }}
                >
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            SPORTS TOURNAMENT <br />MANAGEMENT SOFTWARE
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Making Sports Tournament Management Faster, Smarter, Easier!
                        </p>

                        <div className="flex flex-wrap items-center gap-3">
                            <Link
                                to={"/signup/player/" as any}
                                className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                            >
                                <span className="font-inter text-base font-medium">Lets Get Started</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link
                                to="/find-tournaments/"
                                className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10"
                            >
                                Browse Tournaments
                            </Link>
                        </div>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                            <a
                                href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                                    <img src={iconGoogle} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>

                            <a
                                href="https://apps.apple.com/us/app/score-pal/id6745509167"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={iconApple} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="flex flex-col gap-8 lg:gap-16">
                        <div className="text-center">
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                <span className="text-[#082F8A]">MANAGING</span> TOURNAMENT CHAOS SOLVED
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                      Managing events manually can lead to messy brackets, scheduling conflicts, scoring delays, and time-consuming tournament setup. ScorePal is an advanced online sports tournament management software for clubs and organizers streamline every stage of competition management.
                            </p>
                                <b></b>
                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                      With our online sports tournament software, sports clubs can automate brackets, schedules, live scoring, and results from one centralized platform built for modern tournament management.
                            </p>
                        </div>

                        <div>
                            <img
                                src={sportsLeagueNextLevelBg}
                                alt="Sports tournament management"
                                className="w-full max-h-[400px] rounded-3xl object-contain"
                            />
                        </div>
                        </div>
                    </div>
                </section>

                 <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                    <div className="container mx-auto px-6 lg:px-12">

                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8 text-center">
                            Features of Our Sports <span className="text-[#082F8A]">tournament</span> Software
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                        {features.map((feature, index) => (
                        <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-[#EDEEF4] flex items-center justify-center shrink-0">
                            <img src={feature.icon} alt="" className="w-6 h-6" />
                        </div>

                        <h3 className="font-inter text-[#082F8A] font-semibold text-base">
                            {feature.title}
                        </h3>
                    </div>

                    <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">
                        {feature.description}
                    </p>
                </div>
            ))}
        </div>
    </div>
</section>

            <div className="flex justify-center">
                <Link
                    to={"/signup/player/" as any}
                    className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                >
                    <span className="font-inter text-base font-medium">Lets Get Started</span>
                    <div className="bg-[#082F8A] rounded-full p-1">
                        <ArrowUpRightIcon className="text-white" size={28} />
                    </div>
                </Link>
            </div>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                Why Clubs & Organizers <br />Choose ScorePal?
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed mb-8">
                            ScorePal is built specifically as sports tournament software for clubs that need reliable competition management without operational complexity. Unlike generic event tools, ScorePal supports both tournaments and league management in one platform, making it easier to scale competitions, manage seasons, and handle multiple formats from a single system.
                            </p>

                            <ul className="space-y-4">
                                {whyChooseItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <img
                                src={sportsLeagueFeaturesBg}
                                alt="Why choose ScorePal"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div className="hidden lg:block">
                            <img
                                src={sportsLeagueWhyBg}
                                alt="Take your tournament to the next level"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>

                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                ELEVATE YOUR TOURNAMENT <br />EXPERIENCE
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                ScorePal helps you run faster, smoother, and more professional tournaments. Simplify scheduling, automate scoring and standings, and keep everyone connected—so you can focus on creating unforgettable competition days.
                            </p>
                        </div>

                        <div className="block lg:hidden">
                            <img
                                src={sportsLeagueWhyBg}
                                alt="Take your tournament to the next level"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div
                        className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat"
                        style={{backgroundImage: `url(${ctaBg})`}}
                    >
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,48px)] leading-tight font-bold tracking-wide mb-4">
                            Effortlessly Run Professional Sports Tournaments
                        </h2>
                        <p className="font-inter text-white/90 text-base lg:text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
                            Managing tournaments manually often creates scheduling conflicts, messy brackets, delayed score updates, and unnecessary admin work. With ScorePal, clubs and organizers can manage competitions using powerful sports tournament software built to simplify every stage of tournament operations.
                            <br /><br />
                            Designed as an all-in-one online sports tournament software, ScorePal helps clubs, academies, schools, and organizers create tournaments, automate schedules, manage brackets, track live scoring, and publish results from one centralized platform.
                             <br /><br />
                            Whether you organize local events or large-scale competitions, ScorePal gives you complete tournament control without spreadsheets or manual coordination.
                        </p>
                        <Link
                            to={"/signup/player/" as any}
                            className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                    
                </div>
            </section>
            
            <section className="py-16 lg:py-24 bg-white">
            <div className="container mx-auto px-6 lg:px-12">

            <div className="text-center max-w-5xl mx-auto mb-12">
            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                Manual Tournament Management vs Tournament Management Software
            </h2>

            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                Traditional tournament management often depends on spreadsheets, disconnected tools, and manual coordination, which can slow down operations and increase the risk of errors.
                <b></b>
                With manual systems, organizers typically spend hours managing schedules, updating brackets, coordinating teams, and tracking scores separately. Delayed updates and inconsistent communication can negatively impact the tournament experience for both players and spectators.
                ScorePal replaces manual processes with an automated tournament management system that centralizes scheduling, sports bracket software, scoring, standings, and results in one platform.
                 <b></b>
                Organizers can automate tournament workflows, reduce administrative overhead, improve match visibility, and deliver a smoother competition experience from start to finish.
            </p>
                </div>
            </div>
            </section>


            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
    <div className="container mx-auto px-6 lg:px-12">

        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-14">
            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                How <span className="text-[#082F8A]">ScorePal</span> Sports Tournament Management Software Works
            </h2>

            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                Running tournaments with ScorePal is simple and fully streamlined:
            </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* Step 1 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    1
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Create Your Tournament
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Set up competitions quickly using flexible sports tournament software designed for clubs and organizers.
                </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    2
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Add Teams & Participants
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Register teams, organize divisions, and manage participant information from one centralized dashboard.
                </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    3
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Choose Tournament Formats
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Select knockout, round robin, league, or hybrid formats depending on your competition structure.
                </p>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    4
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Generate Brackets Automatically
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Use the integrated sports bracket software to create organized tournament brackets instantly.
                </p>
            </div>

            {/* Step 5 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    5
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                   Schedule Matches Efficiently
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Automate match scheduling based on venues, timing, and tournament progression.
                </p>
            </div>

            {/* Step 6 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    6
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Track Live Scoring
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Update scores in real time using an integrated live scoring system that automatically refreshes standings and results.
                </p>
            </div>

            {/* Step 7 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    7
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Publish Results & Rankings
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Share standings, rankings, and tournament outcomes instantly across devices.
                </p>
            </div>
               
            </div>
            </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                <div className="flex justify-center">
                <img
                src={infographic}
                alt="How ScorePal Works"
                className="max-w-5xl w-full h-auto rounded-3xl object-contain"
                            />
                </div>
                </div>
            </section>

            <section className="pt-10 max-md:py-10 bg-[#EDEEF4]">
    <div className="container mx-auto px-6 md:px-12">
        <div className="text-center mb-12 relative z-20">
            <h2 className="font-bebas text-[clamp(40px,8vw,64px)] leading-tight mb-4 max-md:mb-2 text-[#1F2022] uppercase">
                Sports Supported by ScorePal
            </h2>

            <div className="flex flex-wrap gap-2 md:text-lg max-sm:text-xl">
                {SPORTS.map((sport) => (
                    <a
                        key={sport.title}
                        href={sport.href}
                        style={{ backgroundImage: `url(${sport.backgroundImage})` }}
                        className="flex-1 flex items-center bg-primary justify-center rounded-xl min-h-40 text-white bg-cover bg-center min-w-32 shadow-lg"
                    >
                        {sport.title}
                    </a>
                ))}
            </div>
        </div>
    </div>
</section>
                <FaqComponent title={"Faqs About Sports Tournament Software"} faqs={FAQs} />
            <FooterSection />
        </div>
    );
}
